import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Snackbar,
    Alert,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { collection, getDocs, addDoc, updateDoc, doc, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { auth } from '../firebaseConfig';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { requestNotificationPermission, sendTestNotification } from '../services/notificationService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutorenewIcon from '@mui/icons-material/Autorenew';

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)'
    }
}));

const ItemCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    }
}));

const CategoryAccordion = styled(Accordion)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: '12px !important',
    '&:before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        margin: '0 0 16px 0',
    },
}));

const CategoryAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    borderRadius: '12px',
    '&.Mui-expanded': {
        minHeight: '48px',
    },
}));

const CategoryAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: '0 0 12px 12px',
}));

const ExpirationList = () => {
    const [items, setItems] = useState([]);
    const [expiredItems, setExpiredItems] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRestocking, setIsRestocking] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        expirationDate: ''
    });

    const categories = [
        'Medication',
        'Equipment',
        'Supply',
        'Other'
    ];

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                fetchItems();
            }
        });

        const checkNotificationPermission = async () => {
            try {
                await requestNotificationPermission();
            } catch (error) {
                console.error('Notification permission error:', error);
                showSnackbar(error.message, 'error');
            }
        };

        checkNotificationPermission();
        return () => unsubscribe();
    }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const userId = auth.currentUser?.uid;
            if (!userId) {
                console.log('No user ID available');
                return;
            }

            const itemsQuery = query(
                collection(db, 'expirationItems'),
                where('userId', '==', userId)
            );

            const snapshot = await getDocs(itemsQuery);
            const currentDate = new Date();
            const itemsList = [];
            const expiredList = [];

            snapshot.forEach(doc => {
                const item = { id: doc.id, ...doc.data() };
                if (item.status === 'deleted') return;
                
                const expirationDate = item.expirationDate.toDate();
                
                if (expirationDate < currentDate) {
                    expiredList.push(item);
                } else {
                    itemsList.push(item);
                }
            });

            itemsList.sort((a, b) => a.expirationDate.toDate() - b.expirationDate.toDate());
            expiredList.sort((a, b) => a.expirationDate.toDate() - b.expirationDate.toDate());

            setItems(itemsList);
            setExpiredItems(expiredList);
        } catch (error) {
            console.error('Error fetching items:', error);
            showSnackbar('Error fetching items: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = async () => {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) {
                showSnackbar('You must be logged in to add items', 'error');
                return;
            }

            const newItem = {
                ...formData,
                userId,
                expirationDate: Timestamp.fromDate(new Date(formData.expirationDate)),
                createdAt: Timestamp.now(),
                status: 'active'
            };

            await addDoc(collection(db, 'expirationItems'), newItem);
            showSnackbar('Item added successfully', 'success');
            setOpenAddDialog(false);
            resetForm();
            fetchItems();
        } catch (error) {
            console.error('Error adding item:', error);
            showSnackbar('Error adding item', 'error');
        }
    };

    const handleUpdateItem = async () => {
        try {
            if (!selectedItem) return;

            const updatedItem = {
                ...formData,
                expirationDate: Timestamp.fromDate(new Date(formData.expirationDate)),
                lastUpdated: Timestamp.now(),
                status: 'active',
                ...(isRestocking ? {
                    restockedAt: Timestamp.now(),
                    restockedBy: auth.currentUser?.uid || null,
                } : {})
            };

            await updateDoc(doc(db, 'expirationItems', selectedItem.id), updatedItem);
            showSnackbar(isRestocking ? 'Item restocked successfully' : 'Item updated successfully', 'success');
            setOpenEditDialog(false);
            resetForm();
            setIsRestocking(false);
            fetchItems();
        } catch (error) {
            console.error('Error updating item:', error);
            showSnackbar('Error updating item', 'error');
        }
    };

    const handleDeleteItem = async (itemId) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        try {
            await updateDoc(doc(db, 'expirationItems', itemId), {
                status: 'deleted',
                deletedAt: Timestamp.now()
            });
            showSnackbar('Item deleted successfully', 'success');
            fetchItems();
        } catch (error) {
            console.error('Error deleting item:', error);
            showSnackbar('Error deleting item', 'error');
        }
    };

    const handleTestNotification = async () => {
        try {
            await sendTestNotification();
            showSnackbar('Test notification sent successfully', 'success');
        } catch (error) {
            console.error('Error sending test notification:', error);
            showSnackbar(error.message, 'error');
            
            if (error.message.includes('permission was denied')) {
                showSnackbar('Please enable notifications in your browser settings and try again', 'warning');
            }
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const resetForm = () => {
        setFormData({
            name: '',
            category: '',
            expirationDate: ''
        });
        setSelectedItem(null);
    };

    const handleOpenEditDialog = (item) => {
        setSelectedItem(item);
        setFormData({
            name: item.name,
            category: item.category,
            expirationDate: item.expirationDate.toDate().toISOString().split('T')[0]
        });
        setOpenEditDialog(true);
    };

    const handleRestock = (item) => {
        setIsRestocking(true);
        handleOpenEditDialog(item);
    };

    const getDaysUntilExpiration = (expirationDate) => {
        const today = new Date();
        const expDate = expirationDate.toDate();
        const diffTime = expDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getTimeSinceExpiration = (expirationDate) => {
        const today = new Date();
        const expDate = expirationDate.toDate();
        const diffTime = today - expDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
            const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
            if (diffHours === 0) {
                const diffMinutes = Math.floor(diffTime / (1000 * 60));
                return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
            }
            return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        }
        
        if (diffDays < 30) {
            return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        }
        
        const diffMonths = Math.floor(diffDays / 30);
        if (diffMonths < 12) {
            return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
        }
        
        const diffYears = Math.floor(diffMonths / 12);
        return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
    };

    const getExpirationStatus = (daysUntilExpiration) => {
        if (daysUntilExpiration <= 7) return 'error';
        if (daysUntilExpiration <= 30) return 'warning';
        return 'success';
    };

    const getItemsExpiringSoon = (items) => {
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        
        return items.filter(item => {
            const expirationDate = item.expirationDate.toDate();
            return expirationDate <= thirtyDaysFromNow && expirationDate > new Date();
        });
    };

	// Returns items expiring within the next `days` days (exclusive of past dates)
	const getItemsExpiringWithinDays = (items, days) => {
		const now = new Date();
		const limit = new Date();
		limit.setDate(limit.getDate() + days);
		return items.filter(item => {
			const exp = item.expirationDate.toDate();
			return exp > now && exp <= limit;
		});
	};

	const formatRibbonItemLabel = (item) => {
		const days = getDaysUntilExpiration(item.expirationDate);
		return `${item.name} (${days}d)`;
	};

    const formatExpiringSoonList = (items) => {
        return items.map(item => {
            const daysUntilExpiration = getDaysUntilExpiration(item.expirationDate);
            return `${item.name} (${daysUntilExpiration} days)`;
        }).join('\n');
    };

    return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			{/* Ribbon: Items expiring within 90 days (top of page) */}
			{(() => {
				const expiring90 = getItemsExpiringWithinDays(items, 90);
				if (expiring90.length === 0) return null;
				const maxShown = 10;
				const shown = expiring90.slice(0, maxShown);
				const remaining = expiring90.length - shown.length;
				return (
					<Box
						sx={{
							mb: 3,
							px: 2,
							py: 1.5,
							borderRadius: '12px',
							background: 'linear-gradient(90deg, rgba(25,118,210,0.08) 0%, rgba(66,165,245,0.08) 100%)',
							border: '1px solid rgba(25,118,210,0.25)',
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							overflowX: 'auto',
							position: 'sticky',
							top: 0,
							zIndex: 1,
							backdropFilter: 'saturate(120%) blur(6px)'
						}}
					>
						<Chip
							label={`${expiring90.length} expiring within 90 days`}
							color="warning"
							size="small"
							sx={{ mr: 1 }}
						/>
						{shown.map(item => (
							<Chip key={item.id} label={formatRibbonItemLabel(item)} size="small" variant="outlined" />
						))}
						{remaining > 0 && (
							<Chip label={`+${remaining} more`} size="small" variant="outlined" />
						)}
					</Box>
				);
			})()}
			<Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1" sx={{ 
                    fontWeight: 'bold',
                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    Expiration Tracker
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Tooltip title="Test Notification">
                        <Button
                            variant="outlined"
                            startIcon={<NotificationsActiveIcon />}
                            onClick={handleTestNotification}
                            sx={{
                                borderRadius: '20px',
                                textTransform: 'none'
                            }}
                        >
                            Test Notification
                        </Button>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenAddDialog(true)}
                        sx={{
                            borderRadius: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                            boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)'
                        }}
                    >
                        Add Item
                    </Button>
                </Box>
            </Box>


            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {/* Active Items Card */}
                    <Grid item xs={12} md={6}>
                        <StyledCard>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                                    Active Items ({items.length})
                                </Typography>
                                {items.length === 0 ? (
                                    <Typography color="text.secondary" align="center">
                                        No active items
                                    </Typography>
                                ) : (
                                    categories.map(category => {
                                        const categoryItems = items.filter(item => item.category === category);
                                        if (categoryItems.length === 0) return null;
                                        const expiringSoonItems = getItemsExpiringSoon(categoryItems);

                                        return (
                                            <CategoryAccordion key={category}>
                                                <CategoryAccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Typography sx={{ flexGrow: 1 }}>
                                                            {category}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            {expiringSoonItems.length > 0 && (
                                                                <Tooltip 
                                                                    title={
                                                                        <Box sx={{ whiteSpace: 'pre-line' }}>
                                                                            {formatExpiringSoonList(expiringSoonItems)}
                                                                        </Box>
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                >
                                                                    <Chip
                                                                        label={`${expiringSoonItems.length} expiring soon`}
                                                                        size="small"
                                                                        color="warning"
                                                                        sx={{ mr: 1 }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                            <Chip
                                                                label={`${categoryItems.length} total`}
                                                                size="small"
                                                                color="primary"
                                                            />
                                                        </Box>
                                                    </Box>
                                                </CategoryAccordionSummary>
                                                <CategoryAccordionDetails>
                                                    {categoryItems.map((item) => {
                                                        const daysUntilExpiration = getDaysUntilExpiration(item.expirationDate);
                                                        const status = getExpirationStatus(daysUntilExpiration);

                                                        return (
                                                            <ItemCard key={item.id} elevation={0}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                        {item.name}
                                                                    </Typography>
                                                                    <Box>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleOpenEditDialog(item)}
                                                                            sx={{ mr: 1 }}
                                                                        >
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDeleteItem(item.id)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Box>
                                                                <Chip
                                                                    label={`${daysUntilExpiration} days until expiration`}
                                                                    color={status}
                                                                    size="small"
                                                                    sx={{ mb: 1 }}
                                                                />
                                                            </ItemCard>
                                                        );
                                                    })}
                                                </CategoryAccordionDetails>
                                            </CategoryAccordion>
                                        );
                                    })
                                )}
                            </CardContent>
                        </StyledCard>
                    </Grid>

                    {/* Expired Items Card */}
                    <Grid item xs={12} md={6}>
                        <StyledCard>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f' }}>
                                    Expired Items ({expiredItems.length})
                                </Typography>
                                {expiredItems.length === 0 ? (
                                    <Typography color="text.secondary" align="center">
                                        No expired items
                                    </Typography>
                                ) : (
                                    categories.map(category => {
                                        const categoryItems = expiredItems.filter(item => item.category === category);
                                        if (categoryItems.length === 0) return null;

                                        return (
                                            <CategoryAccordion key={category}>
                                                <CategoryAccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Typography sx={{ flexGrow: 1 }}>
                                                            {category}
                                                        </Typography>
                                                        <Chip
                                                            label={`${categoryItems.length} expired`}
                                                            size="small"
                                                            color="error"
                                                        />
                                                    </Box>
                                                </CategoryAccordionSummary>
                                                <CategoryAccordionDetails>
                                                    {categoryItems.map((item) => (
                                                        <ItemCard key={item.id} elevation={0}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                    {item.name}
                                                                </Typography>
                                                                <Box>
                                                                    <Tooltip title="Restock / Renew">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleRestock(item)}
                                                                            sx={{ mr: 1 }}
                                                                        >
                                                                            <AutorenewIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleOpenEditDialog(item)}
                                                                        sx={{ mr: 1 }}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteItem(item.id)}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                            <Chip
                                                                label={`Expired ${getTimeSinceExpiration(item.expirationDate)}`}
                                                                color="error"
                                                                size="small"
                                                                sx={{ mb: 1 }}
                                                            />
                                                        </ItemCard>
                                                    ))}
                                                </CategoryAccordionDetails>
                                            </CategoryAccordion>
                                        );
                                    })
                                )}
                            </CardContent>
                        </StyledCard>
                    </Grid>
                </Grid>
            )}

            {/* Add Item Dialog */}
            <Dialog 
                open={openAddDialog} 
                onClose={() => setOpenAddDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add New Item</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                label="Category"
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Expiration Date"
                            type="date"
                            fullWidth
                            value={formData.expirationDate}
                            onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleAddItem} 
                        variant="contained" 
                        color="primary"
                        sx={{
                            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                            boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)'
                        }}
                    >
                        Add Item
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Item Dialog */}
            <Dialog 
                open={openEditDialog} 
                onClose={() => setOpenEditDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Item</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                label="Category"
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Expiration Date"
                            type="date"
                            fullWidth
                            value={formData.expirationDate}
                            onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleUpdateItem} 
                        variant="contained" 
                        color="primary"
                        sx={{
                            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                            boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)'
                        }}
                    >
                        Update Item
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ExpirationList; 